/**
 * `ApiMcpGuideContent` — contenuto della guida al **server MCP**, mostrato come
 * sezione del menù laterale della pagina «Amministrazione» (`/admin/scoring`,
 * sezione «MCP»).
 *
 * Documenta il canale MCP (Model Context Protocol) di sola lettura: doppia
 * autenticazione (`x-api-key` di servizio ad accesso completo oppure login SSO
 * via `Authorization: Bearer <jwt>` con permessi per‑utente), le configurazioni
 * di esempio per il client e il catalogo completo degli strumenti. Ogni
 * strumento è reso come accordion in stile "operation" (riga cliccabile con
 * nome e sintesi; il pannello mostra descrizione, parametri di input e permesso
 * richiesto), analogo alla guida API REST. La gestione delle chiavi vive nella
 * sezione «API keys» della stessa pagina.
 */
import { useId, useState, type ReactNode } from 'react';
import { Card } from '../components/ui';

/** Configurazione del client MCP con la credenziale di SERVIZIO `x-api-key`. */
const MCP_CLIENT_CONFIG_JSON = `{
  "mcpServers": {
    "skills-assessment": {
      "type": "http",
      "url": "<BASE_URL>/mcp",
      "headers": {
        "x-api-key": "<chiave>"
      }
    }
  }
}`;

/** Configurazione del client MCP con login SSO (JWT). */
const MCP_CLIENT_CONFIG_JWT_JSON = `{
  "mcpServers": {
    "skills-assessment": {
      "type": "http",
      "url": "<BASE_URL>/mcp",
      "headers": {
        "Authorization": "Bearer <jwt>"
      }
    }
  }
}`;

/** Capability richiesta da uno strumento (determina il badge del permesso). */
type ToolAccess = 'neutral' | 'levels' | 'cv' | 'self';

/** Etichette leggibili del permesso richiesto da uno strumento. */
const ACCESS_LABEL: Record<ToolAccess, string> = {
  neutral: 'Qualsiasi utente',
  levels: 'Capability Livelli',
  cv: 'Capability CV',
  self: 'Solo login SSO',
};

/** Un parametro di input di uno strumento. */
interface ToolParam {
  name: string;
  type: string;
  required: boolean;
  description: ReactNode;
}

/** Definizione di uno strumento MCP documentato. */
interface ToolDef {
  name: string;
  access: ToolAccess;
  summary: ReactNode;
  params?: ToolParam[];
}

/**
 * Accordion compatto in stile "operation" per un singolo strumento MCP: riga
 * cliccabile (nome + badge permesso + sintesi) che espande il dettaglio
 * (descrizione e parametri di input). L'intera riga è il toggle; accessibile da
 * tastiera (`role="button"`, `aria-expanded`).
 */
function ToolAccordion({ tool }: { tool: ToolDef }) {
  const [open, setOpen] = useState(false);
  const contentId = useId();
  const toggle = () => setOpen((prev) => !prev);

  return (
    <li className="mcp-tool">
      <div
        className="mcp-tool__head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-controls={contentId}
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
      >
        <span className="mcp-tool__icon" aria-hidden="true" />
        <code className="mcp-tool__name">{tool.name}</code>
        <span className={`mcp-tool__access mcp-tool__access--${tool.access}`}>
          {ACCESS_LABEL[tool.access]}
        </span>
        <span className="mcp-tool__summary">{tool.summary}</span>
      </div>

      {open && (
        <div id={contentId} className="mcp-tool__panel">
          {tool.params !== undefined && tool.params.length > 0 ? (
            <>
              <h4 className="api-endpoint__label">Parametri di input</h4>
              <ul className="api-endpoint__params">
                {tool.params.map((p) => (
                  <li key={p.name} className="api-endpoint__param">
                    <code className="api-endpoint__param-name">{p.name}</code>
                    <span className="api-endpoint__param-meta">
                      {p.type}
                      {p.required ? ' · obbligatorio' : ' · facoltativo'}
                    </span>
                    <span className="api-endpoint__param-desc">
                      {p.description}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="api-docs__desc">Nessun parametro di input.</p>
          )}
        </div>
      )}
    </li>
  );
}

/** Un gruppo di strumenti (titolo + elenco di accordion). */
function ToolGroup({ title, tools }: { title: string; tools: ToolDef[] }) {
  return (
    <div className="mcp-tool-group">
      <h3 className="mcp-tool-group__title">{title}</h3>
      <ul className="mcp-tool-list">
        {tools.map((tool) => (
          <ToolAccordion key={tool.name} tool={tool} />
        ))}
      </ul>
    </div>
  );
}

// --- Parametri riusati ------------------------------------------------------
const QUERY_PARAM: ToolParam = {
  name: 'query',
  type: 'string',
  required: true,
  description: 'Testo da cercare (match parziale). Vuoto = catalogo completo.',
};
const SKILL_ID_PARAM: ToolParam = {
  name: 'skillId',
  type: 'string (id)',
  required: true,
  description: 'Id della skill (ottenuto da searchSkills).',
};
const PERSON_ID_PARAM: ToolParam = {
  name: 'personId',
  type: 'string (id)',
  required: true,
  description: 'Id della persona (ottenuto da searchPeople).',
};
const JOB_ID_PARAM: ToolParam = {
  name: 'jobId',
  type: 'string (id)',
  required: true,
  description: 'Id della professione (ottenuto da searchJobs).',
};
const COMPETENCE_ID_PARAM: ToolParam = {
  name: 'competenceId',
  type: 'string (id)',
  required: true,
  description: 'Id della competenza (ottenuto da searchCompetences).',
};
const CANDIDATE_ID_PARAM: ToolParam = {
  name: 'candidateId',
  type: 'string (id)',
  required: true,
  description: 'Id del candidato (ottenuto da searchCandidates).',
};

// --- Cataloghi degli strumenti ---------------------------------------------
const RESOLVER_TOOLS: ToolDef[] = [
  { name: 'searchSkills', access: 'neutral', summary: 'Cerca una skill per nome → id.', params: [QUERY_PARAM] },
  { name: 'searchJobs', access: 'neutral', summary: 'Cerca una professione per nome → id.', params: [QUERY_PARAM] },
  { name: 'searchCompetences', access: 'neutral', summary: 'Cerca una competenza per nome → id.', params: [QUERY_PARAM] },
  { name: 'searchPeople', access: 'levels', summary: 'Cerca una persona per nome o email → id.', params: [QUERY_PARAM] },
  { name: 'searchCandidates', access: 'cv', summary: 'Cerca un candidato (CV) per nome → id.', params: [QUERY_PARAM] },
];

const SKILL_CERT_TOOLS: ToolDef[] = [
  { name: 'listCertifications', access: 'levels', summary: 'Elenco delle certificazioni a catalogo.' },
  { name: 'whoHasSkill', access: 'levels', summary: 'Chi possiede una skill, con il punteggio.', params: [SKILL_ID_PARAM] },
  { name: 'skillCoverage', access: 'levels', summary: 'Persone qualificate e punteggio medio su una skill.', params: [SKILL_ID_PARAM] },
  { name: 'whoHasCertification', access: 'levels', summary: 'Chi possiede una certificazione (per nome/ente).', params: [QUERY_PARAM] },
  { name: 'personSkillProficiency', access: 'levels', summary: 'Proficiency di una persona su una skill, con n° valutazioni.', params: [PERSON_ID_PARAM, SKILL_ID_PARAM] },
];

const CANDIDATE_TOOLS: ToolDef[] = [
  { name: 'listInterestingCandidates', access: 'cv', summary: 'Candidati segnalati come interessanti o da valutare.', params: [{ name: 'signal', type: "'promising' | 'toEvaluate'", required: false, description: 'Filtra per tipo di segnale. Assente = tutti i segnalati.' }] },
  { name: 'candidatesWithSkill', access: 'cv', summary: 'Candidati che possiedono una skill.', params: [SKILL_ID_PARAM] },
  { name: 'candidateProfile', access: 'cv', summary: 'Profilo arricchito di un candidato (KPI, competenze, professioni, certificazioni).', params: [CANDIDATE_ID_PARAM] },
  { name: 'candidatesWithCertification', access: 'cv', summary: 'Candidati che possiedono una certificazione (per nome/ente).', params: [QUERY_PARAM] },
];

const JOB_TOOLS: ToolDef[] = [
  { name: 'personJobMatch', access: 'levels', summary: 'Posizionamento di una persona su una professione.', params: [PERSON_ID_PARAM, JOB_ID_PARAM] },
  { name: 'candidateJobMatch', access: 'cv', summary: 'Posizionamento di un candidato su una professione.', params: [CANDIDATE_ID_PARAM, JOB_ID_PARAM] },
  { name: 'peopleMatchingJob', access: 'levels', summary: 'Dipendenti più adatti a una professione.', params: [JOB_ID_PARAM] },
  { name: 'candidatesMatchingJob', access: 'cv', summary: 'Candidati più adatti a una professione.', params: [JOB_ID_PARAM] },
];

const COMPETENCE_TOOLS: ToolDef[] = [
  { name: 'personCompetenceProficiency', access: 'levels', summary: 'Quanto è brava una persona su una competenza.', params: [PERSON_ID_PARAM, COMPETENCE_ID_PARAM] },
  { name: 'peopleGoodAtCompetence', access: 'levels', summary: 'Dipendenti più bravi su una competenza.', params: [COMPETENCE_ID_PARAM] },
  { name: 'candidatesGoodAtCompetence', access: 'cv', summary: 'Candidati più bravi su una competenza.', params: [COMPETENCE_ID_PARAM] },
];

const SELF_TOOLS: ToolDef[] = [
  { name: 'mySkills', access: 'self', summary: 'Le mie skill valutate con i relativi livelli.' },
  { name: 'myProficiency', access: 'self', summary: 'La mia proficiency su una skill, con n° valutazioni.', params: [SKILL_ID_PARAM] },
  { name: 'myCertifications', access: 'self', summary: 'Le mie certificazioni.' },
  { name: 'myJobMatches', access: 'self', summary: 'Il mio posizionamento su tutte le professioni.' },
  { name: 'myCompetences', access: 'self', summary: 'I miei match per competenza.' },
  { name: 'myProfileSummary', access: 'self', summary: 'Il riepilogo del mio profilo (vista «Il mio CV»).' },
];

export function ApiMcpGuideContent() {
  return (
    <div className="admin-section-stack">
      <Card>
        <h2 className="scoring-admin__section-title">MCP server (assistenti AI)</h2>
        <p className="scoring-admin__section-help">
          Oltre alle API REST, gli stessi dati sono interrogabili da un
          assistente AI tramite un server{' '}
          <a
            href="https://modelcontextprotocol.io"
            target="_blank"
            rel="noreferrer"
          >
            MCP (Model Context Protocol)
          </a>
          , di sola lettura. Le query coprono sia i dipendenti sia i candidati
          (CV caricati), su tre assi: skill, professioni (Job) e competenze.
        </p>
      </Card>

      <Card>
        <h2 className="scoring-admin__section-title">Autenticazione</h2>
        <p className="scoring-admin__section-help">
          Il server accetta due modalità di autenticazione:
        </p>
        <ul className="api-docs__desc">
          <li>
            <strong>API key di servizio</strong> (header <code>x-api-key</code>):
            accesso <strong>completo</strong> a tutti gli strumenti, senza
            vincoli di permesso. È una credenziale di servizio (non personale) e
            può essere creata solo dagli amministratori nella sezione «API keys».
          </li>
          <li>
            <strong>Login SSO (JWT)</strong> (header{' '}
            <code>Authorization: Bearer &lt;jwt&gt;</code>, lo stesso SSO
            dell&apos;applicazione): gli strumenti rispettano i{' '}
            <strong>permessi del tuo utente</strong> (vedi i badge sugli
            strumenti) e si aggiungono gli strumenti personali («i miei dati»).
            Al primo accesso l&apos;utente viene creato senza capability: finché
            un amministratore non le abilita restano disponibili solo gli
            strumenti generali e quelli personali.
          </li>
        </ul>
        <pre className="api-docs__code">
          <code>
            {'POST <BASE_URL>/mcp\n'}
            {'x-api-key: <chiave>            # servizio (accesso completo)\n'}
            {'Authorization: Bearer <jwt>   # oppure login SSO (permessi utente)'}
          </code>
        </pre>
      </Card>

      <Card>
        <h2 className="scoring-admin__section-title">Configurazione del client</h2>
        <p className="scoring-admin__section-help">
          Configurazione standard da incollare nel file del client MCP (formato{' '}
          <code>mcpServers</code>), sostituendo <code>&lt;BASE_URL&gt;</code> e{' '}
          <code>&lt;chiave&gt;</code>. Con API key di servizio:
        </p>
        <pre className="api-docs__code">
          <code>{MCP_CLIENT_CONFIG_JSON}</code>
        </pre>
        <p className="api-docs__desc">
          In alternativa, con login SSO (JWT), sostituendo <code>&lt;jwt&gt;</code>{' '}
          con il proprio token:
        </p>
        <pre className="api-docs__code">
          <code>{MCP_CLIENT_CONFIG_JWT_JSON}</code>
        </pre>
      </Card>

      <Card>
        <h2 className="scoring-admin__section-title">Strumenti disponibili</h2>
        <p className="scoring-admin__section-help">
          Tutti di sola lettura. Clicca uno strumento per vederne i parametri. Il
          badge indica il permesso richiesto con login SSO (con la API key di
          servizio non c&apos;è alcun vincolo).
        </p>

        <ToolGroup
          title="Risolutori (nome → id)"
          tools={RESOLVER_TOOLS}
        />
        <ToolGroup title="Skill e certificazioni (dipendenti)" tools={SKILL_CERT_TOOLS} />
        <ToolGroup title="Candidati (CV caricati)" tools={CANDIDATE_TOOLS} />
        <ToolGroup title="Professioni (Job)" tools={JOB_TOOLS} />
        <ToolGroup title="Competenze" tools={COMPETENCE_TOOLS} />
        <ToolGroup title="Strumenti personali «i miei dati»" tools={SELF_TOOLS} />
      </Card>
    </div>
  );
}
